import {
	ApolloClient,
	ApolloLink,
	CombinedGraphQLErrors,
} from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { LocalState } from "@apollo/client/local-state";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";

import * as defaultQueries from "../queries/defaultQueries";
import { formatGraphQLErrors, formatNetworkError } from "../formatErrors";
import { suppressErrors } from "./ignoreNotImplementedErrors";

/**
 * @param ClientImpl - the `ApolloClient` to build. The Next.js integration ships
 *   its own subclass, which takes part in the server-to-browser data transport,
 *   so the browser provider passes that one. Everything else here is shared.
 */
export const createClient = ({
	cache,
	tenantId,
	getToken,
	onInvalidToken,
	onError,
	uri,
	ClientImpl = ApolloClient,
}) => {
	/**
	 * When a user logs in or creates a new account the first action performed
	 * is a mutation. A mutation response is only saved if there is already an
	 * object in the cache, so we prime the cache with a default (empty) query.
	 */
	function writeDefaultToCache() {
		cache.writeQuery(defaultQueries.INITIALIZE_APP);
	}

	const handleErrors = new ErrorLink(({ error, operation }) => {
		// Set context based on response
		const { response, sentAuthToken } = operation.getContext();
		const transactionId = response?.headers?.get("x-request-id") ?? "";

		const tags = {
			...(transactionId && { "transaction-id": transactionId }),
		};

		// Separate errors by message
		const byIsTokenInvalid = ({ message }) => message === "ErrorTokenInvalid";
		const isErrorNotImplemented = ({ message }) =>
			message === "ErrorNotImplemented";

		// A wrong password, a mistyped MFA code, or an MFA lockout are the user
		// being told what to do next, not an incident. Every screen that can
		// raise one shows it in place, so reporting them here only fills Sentry.
		// Matched exactly, like the two predicates above: jasper-api returns
		// these as bare sentinels, so a message that merely embeds one is a
		// different failure and still belongs in Sentry.
		const expectedAuthErrors = [
			"ErrorMFACodeIncorrect",
			"ErrorMFALocked",
			"LoginAttemptFailed",
		];
		const isExpectedAuthError = ({ message }) =>
			expectedAuthErrors.includes(message);

		const not =
			(pred) =>
			(...args) =>
				!pred(...args);

		// A network error arrives on its own, not wrapped in a GraphQL error list.
		if (!CombinedGraphQLErrors.is(error)) {
			onError(formatNetworkError(error), { tags });
			return;
		}

		const graphQLErrors = error.errors;
		const invalidTokenError = graphQLErrors.find(byIsTokenInvalid);

		const remainingGraphQLErrors = graphQLErrors
			.filter(not(byIsTokenInvalid))
			.filter(not(isErrorNotImplemented))
			.filter(not(isExpectedAuthError));

		if (invalidTokenError && sentAuthToken) {
			onInvalidToken(invalidTokenError, { tags });
		}

		if (remainingGraphQLErrors.length) {
			onError(formatGraphQLErrors(remainingGraphQLErrors), { tags });
		}
	});

	const addAuthorizationHeader = new SetContextLink(({ headers = {} }) => {
		const token = getToken();
		if (token) {
			return {
				sentAuthToken: true,
				headers: {
					...headers,
					"X-Authorization": `Bearer: ${token}`,
					"X-Tenant-ID": tenantId,
				},
			};
		}

		return {
			sentAuthToken: false,
			headers: {
				...headers,
				"X-Tenant-ID": tenantId,
			},
		};
	});

	// apollo-upload-client 20 replaced `createUploadLink` with this class; same
	// options.
	const linkToNetwork = new UploadHttpLink({ uri, credentials: "include" });

	writeDefaultToCache();

	const client = new ClientImpl({
		cache,
		link: ApolloLink.from([
			addAuthorizationHeader,
			suppressErrors,
			handleErrors,
			linkToNetwork,
		]),
		// Apollo Client 4 requires local state to be configured for any document
		// with `@client` fields, even when a cache type policy resolves them, as
		// every one here does (reactive vars, in useInMemoryCache).
		localState: new LocalState({}),
		defaultOptions: {
			// Apollo Client 4 flips this default to `true`, which adds a loading
			// render to every watched query. Keep Apollo Client 3's behaviour so the
			// upgrade stays behaviour-neutral.
			watchQuery: { notifyOnNetworkStatusChange: false },
		},
	});

	client.onClearStore(writeDefaultToCache);
	client.onResetStore(writeDefaultToCache);

	return client;
};
